import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import { volumeAtom } from "~/Atom";
import { PlaySound } from "~/SoundUtils";
import Accordion from "~/components/utils/Accordion";
import NestedAccordion from "~/components/utils/NestedAccordion";
import Modal from "~/components/modal/Modal";
import ConfirmationModal from "~/components/modal/ConfirmationModal";
import KnowledgeDetailModal from "~/components/modal/KnowledgeDetailModal";
import {
  useAwsKnowledgeSection,
  AwsKnowledgeBase,
  AwsKnowledgeFile,
  KbSyncStatus,
} from "./knowledge/hooks/useAwsKnowledgeSection";
import { api } from "~/api";
import "~/css/RadioButton.css";
import "~/css/KnowledgeSetting.css";
import "~/css/VoiceSettings.css";

/**
 * AWSナレッジセクション（Bedrock/S3経由）
 */
const AwsKnowledgeSection: React.FC = () => {
  const { t } = useTranslation();
  const [volume] = useAtom(volumeAtom);
  const {
    knowledgeBases,
    isKbLoading,
    filesByKb,
    fetchFiles,
    addingKbId,
    setAddingKbId,
    inputMethod,
    setInputMethod,
    newName,
    setNewName,
    newText,
    setNewText,
    selectedFile,
    setSelectedFile,
    isDragOver,
    isLoading,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
    handleCancelClick,
    validateFile,
    handleSave,
    handleDeleteFile,
    syncByBedrockKb,
    uniqueBedrockKbs,
    startSync,
    docStatusByKb,
    fetchDocStatus,
    isSuccessModalOpen,
    closeSuccessModal,
    alertModal,
    closeAlertModal,
  } = useAwsKnowledgeSection();

  /**
   * 新規登録フォーム
   */
  const renderRegistrationForm = () => (
    <div className="dict-form-wrapper">
      <div className="dict-form-body">
        {/* ナレッジベース選択 */}
        <div className="form-row">
          <div className="field-inline">
            <span className="field-label">{t("knowledgeBaseSelect")}</span>
            <div className="speakers-dropdown knowledge-dropdown-container">
              <select
                value={addingKbId}
                onChange={(e) => setAddingKbId(e.target.value)}
                disabled={isLoading}
                className="form-select font-default-normal-20"
              >
                <option value="" disabled>
                  {t("knowledgeBasePlaceholder")}
                </option>
                {knowledgeBases.map((kb: AwsKnowledgeBase) => (
                  <option key={kb.kb_id} value={kb.kb_id}>
                    {kb.display_name}
                  </option>
                ))}
              </select>
              <span className="material-symbols-rounded accordion-icon-child knowledge-dropdown-icon">
                stat_minus_1
              </span>
            </div>
          </div>
        </div>

        {/* 入力方法選択（file / text のみ） */}
        <div className="form-row">
          <div className="field-inline">
            <span className="field-label">{t("selectInputMethod")}</span>
            <div className="engine-container">
              <form className="engine-list">
                <div className="radio-row">
                  <label className="custom-radio-label">
                    <input
                      className="custom-radio"
                      type="radio"
                      value="file"
                      checked={inputMethod === "file"}
                      onChange={() => {
                        setInputMethod("file");
                        PlaySound("push2", volume);
                      }}
                      name="awsInputMethod"
                      disabled={isLoading}
                    />
                    <span className="radio-custom-circle"></span>
                    <span className="view-text font-default-normal-20">{t("fileUpload")}</span>
                  </label>
                </div>
                <div className="radio-row">
                  <label className="custom-radio-label">
                    <input
                      className="custom-radio"
                      type="radio"
                      value="text"
                      checked={inputMethod === "text"}
                      onChange={() => {
                        setInputMethod("text");
                        PlaySound("push2", volume);
                      }}
                      name="awsInputMethod"
                      disabled={isLoading}
                    />
                    <span className="radio-custom-circle"></span>
                    <span className="view-text font-default-normal-20">{t("textInput")}</span>
                  </label>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* 登録名 */}
        <div className="form-row">
          <div className="field-inline">
            <span className="field-label">{t("knowledgeRegistrationName")}</span>
            <input
              type="text"
              name="registrationName"
              className="form-input knowledge-form-input-full"
              placeholder={t("knowledgeRegistrationNamePlaceholder")}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              disabled={isLoading}
            />
          </div>
        </div>

        {/* テキスト入力 */}
        {inputMethod === "text" && (
          <div className="form-row">
            <div className="field-inline knowledge-field-vertical">
              <span className="field-label">{t("textInput")}</span>
              <textarea
                name="textContent"
                className="form-input knowledge-textarea-resizable"
                placeholder={t("textPlaceholder")}
                value={newText}
                onChange={(e) => setNewText(e.target.value)}
                disabled={isLoading}
                rows={6}
              />
            </div>
          </div>
        )}

        {/* ファイルアップロード */}
        {inputMethod === "file" && (
          <div className="form-row">
            <div className="field-inline knowledge-file-field-vertical">
              <span className="field-label">{t("fileUpload")}</span>
              <div className="knowledge-file-container-full">
                <input
                  type="file"
                  id="aws-file-input"
                  onChange={(e) => {
                    const file = e.target.files?.[0] || null;
                    if (file && validateFile(file)) {
                      setSelectedFile(file);
                    } else {
                      setSelectedFile(null);
                      e.target.value = "";
                    }
                  }}
                  disabled={isLoading}
                  accept=".txt,.md,.pdf,.html,.xlsx,.xls,.docx,.csv,.htm"
                  className="knowledge-hidden-file-input"
                />
                <div
                  className={`knowledge-custom-file-select ${isLoading ? "disabled" : ""} ${isDragOver ? "drag-over" : ""}`}
                  onClick={() => {
                    if (!isLoading) document.getElementById("aws-file-input")?.click();
                  }}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                >
                  {selectedFile ? (
                    <div className="knowledge-file-info-display">
                      <div className="knowledge-file-name">{selectedFile.name}</div>
                      <div className="knowledge-file-size">
                        ({Math.round(selectedFile.size / 1024)} KB)
                      </div>
                    </div>
                  ) : (
                    <div>
                      <span className="material-symbols-rounded knowledge-upload-icon">
                        upload_file
                      </span>
                      <div className="knowledge-upload-text">
                        {isDragOver ? t("dragAndDropHere") : t("fileSelectPlaceholder")}
                      </div>
                      <div
                        className="knowledge-upload-text"
                        style={{ fontSize: "14px", marginTop: "4px", opacity: 0.7 }}
                      >
                        {t("orClickToSelect")}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ボタン */}
        {addingKbId && (
          <div className="form-actions">
            <button
              type="button"
              className="btn-cancel"
              onClick={handleCancelClick}
              disabled={isLoading}
            >
              {t("cancel")}
            </button>
            <button
              type="button"
              className="btn-regist"
              onClick={handleSave}
              disabled={isLoading || isAnySyncing}
            >
              {t("newRegistration")}
            </button>
          </div>
        )}
      </div>
    </div>
  );

  /**
   * KBごとのファイル一覧テーブル
   */
  const renderFilesTable = (kb: AwsKnowledgeBase) => {
    const prefix = `${kb.kb_identifier}/${kb.s3_suffix}/`;
    const files: AwsKnowledgeFile[] = filesByKb[kb.kb_id] || [];

    return (
      <AwsFilesTable
        kb={kb}
        prefix={prefix}
        files={files}
        isLoading={isLoading}
        isSyncing={isAnySyncing}
        docStatus={docStatusByKb[kb.bedrock_kb_id] ?? {}}
        onMount={() => fetchFiles(kb.kb_id, prefix)}
        onFetchDocStatus={() => fetchDocStatus(kb)}
        onDelete={(key) => handleDeleteFile(kb.kb_id, key, prefix)}
      />
    );
  };

  /**
   * 同期中かどうか判定
   */
  const isAnySyncing = Object.values(syncByBedrockKb).some(
    (s) => s.state === "starting" || s.state === "in_progress"
  );

  /**
   * 同期ボタンの状態・アイコン・ラベルを返すヘルパー
   */
  const getSyncBtnProps = (sync: KbSyncStatus) => {
    const isSyncing = sync.state === "starting" || sync.state === "in_progress";
    if (isSyncing)
      return { state: "syncing", icon: "sync", label: t("syncKnowledgeInProgress"), isSyncing };
    if (sync.state === "complete")
      return {
        state: "complete",
        icon: "check_circle",
        label: t("syncKnowledgeCompleted"),
        isSyncing,
      };
    if (sync.state === "failed" || sync.state === "stopped")
      return { state: "failed", icon: "error", label: t("syncKnowledgeFailed"), isSyncing };
    return { state: "idle", icon: "sync", label: t("syncKnowledge"), isSyncing };
  };

  const renderContent = () => {
    const accordionItems = knowledgeBases.map((kb: AwsKnowledgeBase) => ({
      title: kb.display_name,
      content: renderFilesTable(kb),
      dynamicHeight: true,
      isChildIcon: true,
    }));

    return (
      <div className="knowledge-scroll-container">
        <NestedAccordion
          items={[
            {
              title: t("newKnowledgeRegistration"),
              content: renderRegistrationForm(),
              dynamicHeight: true,
              defaultOpen: false,
            },
          ]}
        />
        {knowledgeBases.length > 0 && (
          <>
            {/* KB単位の同期ボタン（データソース単位で1回実行） */}
            {uniqueBedrockKbs.map((kb) => {
              const sync = syncByBedrockKb[kb.bedrock_kb_id] ?? { state: "idle", jobId: null };
              const {
                state: btnState,
                icon: btnIcon,
                label: btnLabel,
                isSyncing,
              } = getSyncBtnProps(sync);
              return (
                <div key={kb.bedrock_kb_id} className="knowledge-sync-row">
                  <button
                    type="button"
                    className={`btn-sync-knowledge btn-sync-knowledge--${btnState}`}
                    onClick={() => startSync(kb)}
                    disabled={isSyncing}
                  >
                    <span
                      className={`material-symbols-rounded${isSyncing ? " knowledge-sync-spin" : ""}`}
                    >
                      {btnIcon}
                    </span>
                    {btnLabel}
                  </button>
                </div>
              );
            })}
            <div className="knowledge-accordion-container">
              <Accordion items={accordionItems} />
            </div>
          </>
        )}
        {isKbLoading && (
          <p className="loadingMessage knowledge-status-message">{t("loadingKnowledgeBases")}</p>
        )}
        {!isKbLoading && knowledgeBases.length === 0 && (
          <p className="loadingMessage knowledge-status-message">{t("noKnowledgeBases")}</p>
        )}
      </div>
    );
  };

  return (
    <div className="knowledge-section-container">
      <Accordion
        items={[
          {
            title: t("knowledgeSettings"),
            content: renderContent(),
            dynamicHeight: true,
            autoHeight: true,
          },
        ]}
      />
      <Modal
        title={t("dataAdded")}
        isOpen={isSuccessModalOpen}
        onClose={closeSuccessModal}
        width={600}
        height={400}
      >
        <div className="knowledge-modal-body">
          <p>{t("dataAdded")}</p>
          <p>{t("syncToReflectKnowledge")}</p>
        </div>
        <div className="modal-footer">
          <button type="button" className="button-1" onClick={closeSuccessModal}>
            {t("close")}
          </button>
        </div>
      </Modal>
      <Modal
        title={t("error")}
        isOpen={alertModal.isOpen}
        onClose={closeAlertModal}
        width={600}
        height={400}
      >
        <div className="knowledge-modal-body">
          <p style={{ whiteSpace: "pre-line" }}>{alertModal.message}</p>
        </div>
        <div className="modal-footer">
          <button type="button" className="button-1" onClick={closeAlertModal}>
            {t("close")}
          </button>
        </div>
      </Modal>
    </div>
  );
};

/**
 * AWSファイル一覧テーブル（アコーディオン展開時にフェッチ）
 */
interface AwsFilesTableProps {
  kb: AwsKnowledgeBase;
  prefix: string;
  files: AwsKnowledgeFile[];
  isLoading: boolean;
  isSyncing: boolean;
  docStatus: Record<string, string>;
  onMount: () => void;
  onFetchDocStatus: () => void;
  onDelete: (key: string) => void;
}

const AwsFilesTable: React.FC<AwsFilesTableProps> = ({
  files,
  isLoading,
  isSyncing,
  docStatus,
  onMount,
  onFetchDocStatus,
  onDelete,
}) => {
  const { t } = useTranslation();
  const [modal, setModal] = useState<{
    isOpen: boolean;
    content: string | null;
    isLoading: boolean;
    error: string | null;
    title: string;
  }>({ isOpen: false, content: null, isLoading: false, error: null, title: "" });
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<AwsKnowledgeFile | null>(null);

  useEffect(() => {
    onMount();
    onFetchDocStatus();
  }, []);

  const handleDetailClick = async (file: AwsKnowledgeFile) => {
    setModal({ isOpen: true, content: null, isLoading: true, error: null, title: file.name });
    try {
      const result = await api.knowledge.fileContent.query({ key: file.key });
      if (result.isBinary) {
        setModal((prev) => ({ ...prev, content: t("binaryFileNotPreviewable"), isLoading: false }));
      } else {
        setModal((prev) => ({
          ...prev,
          content: result.content || t("contentNotAvailable"),
          isLoading: false,
        }));
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : t("unexpectedErrorOccurred");
      setModal((prev) => ({ ...prev, error: message, isLoading: false }));
    }
  };

  const handleDeleteClick = (file: AwsKnowledgeFile) => {
    setFileToDelete(file);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (fileToDelete) {
      onDelete(fileToDelete.key);
      setIsDeleteModalOpen(false);
      setFileToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
    setFileToDelete(null);
  };

  if (isLoading) return <p className="loadingMessage">{t("loading")}</p>;

  return (
    <div className="knowledgeBaseSection aws-files-section">
      <div className="knowledge-table-wrapper">
        <div className="knowledge-table-header">
          <div className="knowledge-col name">{t("knowledgeRegistrationName")}</div>
          <div className="knowledge-col format">{t("urlFormat")}</div>
          <div className="knowledge-col date">{t("creationDate")}</div>
          <div className="knowledge-col sync-status">{t("syncKnowledge")}</div>
          <div className="knowledge-col actions"></div>
          <div className="knowledge-col actions"></div>
        </div>
        <div className="knowledge-table-body">
          {files.length === 0 ? (
            <div className="knowledge-row empty">
              <div className="knowledge-col">{t("noDocuments")}</div>
            </div>
          ) : (
            files.map((file) => {
              const ext = file.name.includes(".")
                ? (file.name.split(".").pop()?.toLowerCase() ?? "unknown")
                : "unknown";
              return (
                <div key={file.key} className="knowledge-row">
                  <div className="knowledge-col name">{file.name}</div>
                  <div className="knowledge-col format">
                    <div className="file-format-container">
                      <span className={`format-badge format-${ext}`}>{ext}</span>
                      <span className="file-type">{t("file")}</span>
                    </div>
                  </div>
                  <div className="knowledge-col date">
                    {new Date(file.lastModified).toLocaleString("ja-JP")}
                  </div>
                  <div className="knowledge-col sync-status">
                    {docStatus[file.key] === "INDEXED" && (
                      <span className="knowledge-indexed-badge">
                        <span className="material-symbols-rounded">check_circle</span>
                        {t("syncKnowledgeCompleted")}
                      </span>
                    )}
                  </div>
                  <div className="knowledge-col actions">
                    <button
                      type="button"
                      className="knowledge-detail-button"
                      onClick={() => handleDetailClick(file)}
                      disabled={isLoading}
                      title={`${file.name}${t("showDetail")}`}
                    >
                      {t("detail")}
                    </button>
                  </div>
                  <div className="knowledge-col actions">
                    <button
                      type="button"
                      className="knowledge-delete-doc-button"
                      onClick={() => handleDeleteClick(file)}
                      disabled={isLoading || isSyncing}
                      title={`${file.name}${t("delete")}`}
                    >
                      {t("delete")}
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
      <KnowledgeDetailModal
        title={modal.title}
        content={modal.content || ""}
        isOpen={modal.isOpen}
        onClose={() =>
          setModal({ isOpen: false, content: null, isLoading: false, error: null, title: "" })
        }
        isLoading={modal.isLoading}
        error={modal.error}
      />
      <ConfirmationModal
        show={isDeleteModalOpen}
        handleClose={handleDeleteCancel}
        handleConfirm={handleDeleteConfirm}
        title={t("delete")}
        body={fileToDelete ? `「${fileToDelete.name}」${t("deleteConfirmation")}` : ""}
        cancelText={t("cancel")}
        confirmText={t("delete")}
      />
    </div>
  );
};

export default AwsKnowledgeSection;
